import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Paper, Divider, Stack } from '@mui/material';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment"; 

// Rutas y Servicios
import { api } from '../../controllers/ConsumeApiController'; // Tu cliente API
import { stripePromisePK } from '../../apiRoutes/apiBase'; 
import WebsiteRoutes from '../../apiRoutes/WebsiteRoutes';
import { payOrder } from '../../controllers/customer/ClientPaymentController';

// Componentes Hijos
import PaymentMethodTabs from './PaymentMethodTabs';
import StripePaymentForm from './forms/StripePaymentForm';
import BancoPlazaForm from './forms/BancoPlazaForm';

const stripePromise = loadStripe(stripePromisePK);

export default function PaymentContainer({ order, merchantUuid, customer, onPaymentSuccess }) {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Datos auxiliares
    const [exchangeRate, setExchangeRate] = useState(0);
    const [banksVe, setBanksVe] = useState([]);
    const [availableCurrencies, setAvailableCurrencies] = useState([]);
    
    // 1. Cargar Métodos de Pago
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Usamos la ruta para obtener métodos de pago del comercio
                const url = WebsiteRoutes.Merchants.GetPaymentMethods + merchantUuid;
                const response = await api.get(url);
                
                if (response.success) {
                    setPaymentMethods(response.data.payment_methods);
                    setExchangeRate(parseFloat(response.data.exchange_rate));
                    setBanksVe(response.data.banks_ve || []);
                    
                    const stripeMethod = response.data.payment_methods.find(m => m.name === 'Stripe');
                    setSelectedMethod(stripeMethod || response.data.payment_methods[0]);

                    setAvailableCurrencies(response.data.currencies || []);
                }
            } catch (error) {
                console.error("Error loading payment methods", error);
            } finally {
                setIsLoadingData(false);
            }
        };
        fetchData();
    }, [merchantUuid]);

    /*
    |--------------------------------------------------------------------------
    | PROCESAR EL PAGO (FLUJO DE RECARGA + COBRO)
    |--------------------------------------------------------------------------
    */
    const handleProcessPayment = async (paymentDataFromForm) => {
        setIsProcessing(true);

        try {
            // Validaciones
            if (!selectedMethod || !selectedMethod.accounts || selectedMethod.accounts.length === 0) {
                alert("Error: Este método de pago no tiene cuentas configuradas.");
                setIsProcessing(false);
                return;
            }

            const bankAccountSelected = selectedMethod.accounts[0]; 
            const fecha = moment().format('YYYY-MM-DD');

            // --- LÓGICA DINÁMICA DE MONEDA ---
            const orderCurrencyIso = order.moneda || 'USD'; // Ej: 'COP'
            
            // Buscamos la moneda en la lista que trajo el backend
            const currencyObj = availableCurrencies.find(c => c.code_iso === orderCurrencyIso);
            
            // Si la encontramos, usamos su ID real. Si no, fallback a 2 (USD) o error.
            const orderCurrencyId = currencyObj ? currencyObj.id : 2;

            // --- PASO 1: RECARGA DE SALDO (Usando API Legacy) ---
            // Construimos el payload tal cual lo espera tu backend actual
            let payload = {
                order_id: order.id_pedido, 
                amount: parseFloat(order.monto), // Monto total (incluye comisión)
                account_number_id: bankAccountSelected.id,
                bank_id: bankAccountSelected.bank_id,
                currency_id: orderCurrencyId, 
                currency:  orderCurrencyIso,
                gateway_id: 'WEB',
                create_real_date: fecha,
                transaction_type_id: 1, 
                customer: customer, 
                merchant_uuid: merchantUuid
            };

            if (paymentDataFromForm.type === 'stripe') {
                payload.provider = 'stripe';
                payload.payment_method_id = paymentDataFromForm.payment_method_id; 
            } 
            else if (paymentDataFromForm.type === 'pago_movil') {
                payload.provider = 'bancoplaza';
                payload.banco = paymentDataFromForm.bank;
                payload.telefonoCliente = paymentDataFromForm.phone;
                payload.referencia = paymentDataFromForm.reference;
                payload.amount_ves = (payload.amount * exchangeRate).toFixed(2);
            }

            // Llamada al endpoint de Recarga (Deposit)
            const urlRecarga = WebsiteRoutes.Stripe.paymentProccess; 
            const responseRecarga = await api.post(urlRecarga, payload);

            if (responseRecarga.success) {
                
                // --- PASO 2: EJECUTAR PAGO DEL PEDIDO (Usando Nueva API) ---
                try {
                    // El dinero ya está en la wallet del usuario, ahora pagamos la orden
                    const responsePago = await payOrder(order.id_pedido);

                    if (responsePago.success) {
                        // ¡Ciclo completo!
                        onPaymentSuccess(responsePago.data);
                    } else {
                        // Caso Borde: Se recargó pero falló el pago interno
                        console.error("Error en payOrder:", responsePago);
                        alert("Tu saldo fue recargado exitosamente, pero hubo un error al aplicar el pago a la suscripción. Por favor contacta a soporte o intenta pagar nuevamente (se usará tu saldo).");
                        // Aún así cerramos el modal porque el dinero ya salió de su banco
                        onPaymentSuccess(null); 
                    }
                } catch (payError) {
                    console.error("Excepción en payOrder:", payError);
                    alert("Error crítico al finalizar el pago de la suscripción.");
                }

            } else {
                alert(responseRecarga.message || "Error al procesar el pago con el banco.");
            }

        } catch (error) {
            console.error(error);
            alert("Ocurrió un error de conexión al procesar el pago.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoadingData) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

    return (
        <Paper elevation={0} sx={{ p: 0 }}>
            
            {/* --- RESUMEN DEL PEDIDO CON DETALLES (COMISIÓN VISIBLE) --- */}
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3, border: '1px solid #eee' }}>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="textSecondary">
                        Pedido #{order.codigo}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {moment(order.created_at).format('DD/MM/YYYY')}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Lista de Ítems (Aquí aparecerá el Plan y la Comisión) */}
                <Stack spacing={1} mb={2}>
                    {order.details && order.details.length > 0 ? (
                        order.details.map((detail, index) => (
                            <Box key={index} display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                        {detail.producto}
                                    </Typography>
                                    {detail.descripcion && (
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {detail.descripcion}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography variant="body2" fontWeight="bold">
                                    ${parseFloat(detail.monto).toFixed(2)}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" color="textSecondary">Pago de servicio</Typography>
                    )}
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* Total Final */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Total a Pagar:</Typography>
                    <Box textAlign="right">
                        <Typography variant="h5" color="primary" fontWeight="bold">
                            ${parseFloat(order.monto).toFixed(2)}
                        </Typography>
                        
                        {selectedMethod?.name === 'Pago Movil' && (
                            <Typography variant="caption" display="block" sx={{ lineHeight: 1 }}>
                                Bs. {(order.monto * exchangeRate).toFixed(2)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
            {/* ------------------------------------------------------- */}

            <Typography variant="h6" gutterBottom align="center" sx={{mb:2}}>
                Selecciona un método de pago
            </Typography>

            <PaymentMethodTabs 
                methods={paymentMethods} 
                selected={selectedMethod} 
                onSelect={setSelectedMethod} 
            />

            <Box sx={{ mt: 2, p: 1 }}>
                {selectedMethod?.name === 'Stripe' && (
                    <Elements stripe={stripePromise}>
                        <StripePaymentForm 
                            amount={order.monto} 
                            onSubmit={handleProcessPayment}
                            isLoading={isProcessing}
                        />
                    </Elements>
                )}

                {selectedMethod?.name === 'Pago Movil' && (
                    <BancoPlazaForm 
                        amount={order.monto}
                        exchangeRate={exchangeRate}
                        banksList={banksVe}
                        onSubmit={handleProcessPayment}
                        isLoading={isProcessing}
                    />
                )}
            </Box>
        </Paper>
    );
}